import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Download, Search, UserPlus } from 'lucide-react';
import { api, type X5User, type X5UserIdentity } from '../lib/api';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { SortableHeader } from '../components/ui/SortableHeader';
import { useSort } from '../lib/sort';
import { useOrgLookup } from '../lib/orgLookup';
import { useAuth } from '../contexts/use-auth';
import { CreateUserModal } from '../components/CreateUserModal';
import { UserDetailDrawer } from '../components/UserDetailDrawer';

type EnrichedUser = X5User & {
  organizationName: string;
  email: string | null;
  cognitoStatus: string | null;
  cognitoLastActive: string | null;
};

const getUserValue = (item: EnrichedUser, key: string): unknown => {
  switch (key) {
    case 'username':
      return item.username ?? '';
    case 'email':
      return item.email ?? '';
    case 'organization':
      return item.organizationName;
    case 'id':
      return item.id;
    case 'createdAt':
      return item.createdAt ?? '';
    default:
      return undefined;
  }
};

export function Users() {
  const { can } = useAuth();
  const canCreate = can('x5.write');

  const [q, setQ] = useState('');
  const [opened, setOpened] = useState<EnrichedUser | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.listUsers(),
  });

  const identities = useQuery({
    queryKey: ['users', 'identities'],
    queryFn: () => api.listUserIdentities(),
    staleTime: 5 * 60 * 1000,
  });

  const lookup = useOrgLookup();

  const identityByUsername = useMemo(() => {
    const map = new Map<string, X5UserIdentity>();
    for (const id of identities.data?.items ?? []) {
      map.set(id.username.toLowerCase(), id);
    }
    return map;
  }, [identities.data]);

  const enriched = useMemo<EnrichedUser[]>(
    () =>
      (data?.items ?? []).map((u) => {
        const id = u.username ? identityByUsername.get(u.username.toLowerCase()) : undefined;
        return {
          ...u,
          organizationName: lookup.resolveName(u.organizationId),
          email: id?.email ?? null,
          cognitoStatus: id?.status ?? null,
          cognitoLastActive: id?.userLastModifiedDate ?? null,
        };
      }),
    [data, lookup, identityByUsername],
  );

  const filtered = useMemo(() => {
    if (!q.trim()) return enriched;
    const needle = q.toLowerCase();
    return enriched.filter(
      (u) =>
        u.username?.toLowerCase().includes(needle) ||
        u.email?.toLowerCase().includes(needle) ||
        u.organizationName.toLowerCase().includes(needle) ||
        u.organizationId?.toLowerCase().includes(needle) ||
        u.id.toLowerCase().includes(needle),
    );
  }, [enriched, q]);

  const { sortedItems, sort, toggle } = useSort(filtered, getUserValue);

  function downloadCsv() {
    const headers = [
      'username',
      'email',
      'organizationName',
      'organizationId',
      'userId',
      'cognitoStatus',
      'cognitoLastActive',
      'createdAt',
      'updatedAt',
    ];
    const lines = [headers.join(',')];
    for (const u of sortedItems) {
      lines.push(
        [
          csvEscape(u.username ?? ''),
          csvEscape(u.email ?? ''),
          csvEscape(u.organizationName),
          csvEscape(u.organizationId ?? ''),
          csvEscape(u.id),
          csvEscape(u.cognitoStatus ?? ''),
          csvEscape(u.cognitoLastActive ?? ''),
          csvEscape(u.createdAt ?? ''),
          csvEscape(u.updatedAt ?? ''),
        ].join(','),
      );
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `x5-users-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">X5 Users</h1>
          <p className="text-muted-foreground">
            {data
              ? `${filtered.length} of ${data.items.length} users${identities.isLoading ? ' · loading identities…' : ''}`
              : '—'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by username, email, organization…"
              className="pl-9"
            />
          </div>
          <Button variant="outline" onClick={downloadCsv} disabled={sortedItems.length === 0}>
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
          {canCreate && (
            <Button onClick={() => setCreateOpen(true)}>
              <UserPlus className="h-4 w-4" />
              Add user
            </Button>
          )}
        </div>
      </div>

      {isLoading && <p className="text-muted-foreground">Loading…</p>}
      {error && <p className="text-destructive">{(error as Error).message}</p>}

      {data && (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <SortableHeader
                    label="Username"
                    sortKey="username"
                    sort={sort}
                    onToggle={toggle}
                  />
                  <SortableHeader label="Email" sortKey="email" sort={sort} onToggle={toggle} />
                  <SortableHeader
                    label="Organization"
                    sortKey="organization"
                    sort={sort}
                    onToggle={toggle}
                  />
                  <SortableHeader label="User ID" sortKey="id" sort={sort} onToggle={toggle} />
                  <SortableHeader
                    label="Created"
                    sortKey="createdAt"
                    sort={sort}
                    onToggle={toggle}
                  />
                </tr>
              </thead>
              <tbody>
                {sortedItems.map((u) => (
                  <tr
                    key={u.id}
                    onClick={() => setOpened(u)}
                    className="border-b border-border last:border-0 hover:bg-muted/50 transition-colors cursor-pointer"
                  >
                    <td className="px-4 py-3 font-medium">{u.username ?? '—'}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {u.email ?? (identities.isLoading ? <span className="opacity-50">…</span> : '—')}
                    </td>
                    <td className="px-4 py-3">
                      {u.organizationId ? (
                        <Link
                          to={`/organizations/${u.organizationId}`}
                          className="text-primary hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {u.organizationName}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{u.id}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {u.createdAt ? formatDate(u.createdAt) : '—'}
                    </td>
                  </tr>
                ))}
                {sortedItems.length === 0 && (
                  <tr>
                    <td className="px-4 py-6 text-center text-muted-foreground" colSpan={5}>
                      No matches.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {opened && <UserDetailDrawer user={opened} onClose={() => setOpened(null)} />}

      {createOpen && <CreateUserModal onClose={() => setCreateOpen(false)} />}
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

function csvEscape(value: string): string {
  if (value == null) return '';
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}
